<template>
  <div class="flex gap-3 sm:gap-5 text-stone-800 text-sm tracking-wider">
    <DropdownLocaleSwitcher
      :icon-size="22"
      text-class="text-stone-800 text-sm"
      open-direction="down"
      hide-text-below-840
      class="hidden md:flex items-center gap-[8px]"
    />
    <ActiveOffersMenu />
    <Menu v-slot="{ close }">
      <div class="relative flex">
        <MenuButton
          v-if="!hideAccountIcon"
          aria-label="user menu"
          @click="handleClickAccount(close)"
        >
          <div class="relative flex items-center gap-[6px] hover:underline">
            <span class="navbar-icon flex w-[22px] h-[22px] items-center">
              <IconUser
                :size="20"
                :fill="
                  route.name?.toString().startsWith('account')
                    ? 'currentColor'
                    : 'none'
                "
              />
            </span>
            <span class="hidden min-[840px]:block">{{
              t('general.account')
            }}</span>
          </div>
        </MenuButton>
        <MenuItems
          v-if="isAuthenticated"
          class="z-10 bg-white divide-y divide-gray-100 rounded-lg shadow w-44 absolute right-0 top-8"
        >
          <div class="py-2 text-sm">
            <MenuItem
              v-slot="{ active }"
              class="block px-4 py-2 hover:bg-gray-100"
            >
              <nuxt-link
                :class="{ 'bg-blue-500': active }"
                class="flex align-middle gap-2 w-full"
                :to="localePath('/account')"
              >
                <div class="flex w-[22px] h-[22px] items-center">
                  <IconSettings size="16" />
                </div>
                {{ t('general.account') }}
              </nuxt-link>
            </MenuItem>
            <MenuItem
              v-if="isAuthenticated"
              v-slot="{ active }"
              class="block px-4 py-2 hover:bg-gray-100"
            >
              <button
                :class="{ 'bg-blue-500': active }"
                class="flex align-middle gap-2 w-full"
                to="/account-settings"
                @click.prevent="handleClickLogOut"
              >
                <div class="flex w-[22px] h-[22px] items-center">
                  <IconBoxArrowLeft size="16" />
                </div>
                {{ t('general.logout') }}
              </button>
            </MenuItem>
          </div>
        </MenuItems>
      </div>
    </Menu>
    <div
      v-show="wishlist?.items?.length > 0"
      class="md:flex relative items-center gap-[8px] cursor-pointer hover:underline"
      data-test-id="navbar-cart-icon"
      @click="() => toggleWishlistSidebar(true)"
    >
      <span class="navbar-icon flex w-[20px] h-[20px] items-center">
        <IconWishlist :size="22" class="wishlist-icon" />
      </span>
      <span class="hidden min-[840px]:block">{{ t('general.wishlist') }}</span>
    </div>
    <div
      class="relative flex items-center gap-[8px] cursor-pointer hover:underline"
      data-test-id="navbar-cart-icon"
      @click="toggleCartSidebar(true)"
    >
      <span class="navbar-icon flex w-[20px] h-[20px] items-center">
        <IconCart
          :size="22"
          :fill="cart.items.length > 0 ? 'currentColor' : 'none'"
        />
      </span>
      <span class="hidden min-[840px]:block">
        {{ t('general.bottomNavigationCart') }}
      </span>
      <client-only>
        <div v-if="cartGetters.getTotalItems(cart) > 0" class="cart-badge">
          {{ cartGetters.getTotalItems(cart) }}
        </div>
      </client-only>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  provideUseId,
} from '@headlessui/vue'
import IconUser from '~/assets/icons/icon-user.svg?component'
import IconWishlist from '~/assets/icons/wishlist.svg?component'
import IconCart from '~/assets/icons/icon-cart.svg?component'
import IconBoxArrowLeft from '@/components/icons/IconBoxArrowLeft.vue'
import IconSettings from '@/components/icons/IconSettings.vue'
import { cartGetters } from '~/utils/cartGetters'
import { sendLogout } from '~/composables/useGtmEvents'

const { isAuthenticated, logout } = useAuth()
const { toggleCartSidebar } = useUiState()
const $localePath = useLocalePath()
const localePath = $localePath
const route = useRoute()

const { t } = useI18n()

const { wishlist } = useWishlist()
const { toggle: toggleWishlistSidebar } = useWishlistSidebarUiState()

const { hideAccountIcon } = defineProps({
  hideAccountIcon: {
    type: Boolean,
    default: false,
  },
})

const { cart } = useCartWithSubdomainLogic()

// Menu close function, received from the Menu slot
const menuCloseFunction = ref<(() => void) | null>(null)

// Tracking route changes to close the menu
watch(
  () => route.fullPath,
  () => {
    if (menuCloseFunction.value) {
      menuCloseFunction.value()
    }
  }
)

const handleClickAccount = (close: () => void) => {
  menuCloseFunction.value = close
  if (!isAuthenticated.value) {
    navigateTo(localePath('/login'))
  }
}

const handleClickLogOut = () => {
  sendLogout()
  logout()
}

// This is a workaround for the headlessui bug where MenuButton
// causes a warning in the console about hydration mismatch.
// https://github.com/tailwindlabs/headlessui/issues/2913
provideUseId(() => useId())
</script>
<style>
.cart-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  min-width: 1rem;
  min-height: 1rem;
  padding: 2px 3px 2px 5px;
  border-radius: 9999px;
  @apply bg-orange-500 text-white;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 0.75rem;
}
</style>

<style lang="postcss" scoped>
.navbar-icon {
  @apply cursor-pointer;

  svg {
    @apply text-stone-600 stroke-stone-600;
    stroke-width: 1.67;
  }

  &:hover {
    svg {
      @apply scale-110 transition-all duration-200;
    }
  }
}

.wishlist-icon {
  :deep(path) {
    @apply duration-300 ease-in-out;
  }

  &:hover :deep(path) {
    @apply stroke-red-500;
  }
}
</style>
